import React from 'react';
import Icon from '../../../components/AppIcon';

const AccommodationSelector = ({ selectedStandard, onStandardSelect, error }) => {
  const accommodationStandards = [
    {
      id: 'budget',
      name: 'Budget',
      stars: 1,
      description: 'Basic accommodation with essential amenities',
      features: ['Clean rooms', 'Basic facilities', 'Good location', 'Value for money'],
      icon: 'Home'
    },
    {
      id: '3-star',
      name: '3-Star',
      stars: 3,
      description: 'Comfortable mid-range accommodation',
      features: ['Room service', 'Restaurant', 'WiFi', 'Daily housekeeping'],
      icon: 'Building'
    },
    {
      id: '4-star',
      name: '4-Star',
      stars: 4,
      description: 'Superior accommodation with excellent service',
      features: ['Concierge', 'Spa/Fitness', 'Multiple dining', 'Premium amenities'],
      icon: 'Building2'
    },
    {
      id: '5-star',
      name: '5-Star',
      stars: 5,
      description: 'Luxury accommodation with world-class service',
      features: ['Butler service', 'Fine dining', 'Luxury spa', 'Exclusive experiences'],
      icon: 'Crown'
    }
  ];

  const renderStars = (count) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={16}
        className={index < count ? 'text-accent fill-current' : 'text-muted-foreground'}
      />
    ));
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          What accommodation standard do you prefer?
        </h3>
        <p className="text-muted-foreground mb-6">
          Choose the level of comfort and service you'd like for your stay
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {accommodationStandards?.map((standard) => (
          <button
            key={standard?.id}
            onClick={() => onStandardSelect(standard?.id)}
            className={`p-6 rounded-lg border-2 text-left transition-all hover:scale-102 ${
              selectedStandard === standard?.id
                ? 'border-primary bg-primary/5 shadow-md'
                : 'border-border hover:border-primary/50 hover:bg-muted/50'
            }`}
          >
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${
                    selectedStandard === standard?.id 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    <Icon name={standard?.icon} size={20} />
                  </div>
                  <div>
                    <h4 className={`font-semibold text-lg ${
                      selectedStandard === standard?.id ? 'text-primary' : 'text-foreground'
                    }`}>
                      {standard?.name}
                    </h4>
                    <div className="flex items-center space-x-1 mt-1">
                      {renderStars(standard?.stars)}
                    </div>
                  </div>
                </div>
                {selectedStandard === standard?.id && (
                  <div className="bg-primary text-primary-foreground rounded-full p-1">
                    <Icon name="Check" size={16} />
                  </div>
                )}
              </div>

              {/* Description */}
              <p className="text-sm text-muted-foreground">
                {standard?.description}
              </p>

              {/* Features */}
              <div className="space-y-2">
                <h5 className="text-sm font-medium text-foreground">Key Features:</h5>
                <div className="grid grid-cols-1 gap-1">
                  {standard?.features?.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Icon 
                        name="Check" 
                        size={12} 
                        className={selectedStandard === standard?.id ? 'text-primary' : 'text-muted-foreground'} 
                      />
                      <span className="text-xs text-muted-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
      {/* Selection Summary */}
      {selectedStandard && (
        <div className="bg-primary/10 p-4 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="Building" size={20} className="text-primary" />
            <span className="font-semibold text-primary">
              Selected: {accommodationStandards?.find(s => s?.id === selectedStandard)?.name} Accommodation
            </span>
          </div>
        </div>
      )}
      {error && (
        <p className="text-error text-sm">{error}</p>
      )}
    </div>
  );
};

export default AccommodationSelector;